import { useCallback, useEffect, useState } from "react";
import { ApiError, apiGet } from "./customFetch";

/** Spejler /api/features-svaret — kun ENABLED features optræder. */
export interface FeatureInfo {
  key: string;
  name: string;
  navPlacement: "main" | "settings" | "none";
}

export interface FeaturesState {
  features: FeatureInfo[];
  loading: boolean;
  /** "unauthorized" ved 401 (ingen tenant-kontekst), ellers generel fejl. */
  error: "unauthorized" | "failed" | null;
  retry: () => void;
}

const FEATURES_UPDATED_EVENT = "appsmeden:features-updated";

export function notifyFeaturesUpdated(): void {
  window.dispatchEvent(new Event(FEATURES_UPDATED_EVENT));
}

/**
 * Frontend-modstykket til discovery: navigationen bygges UDELUKKENDE af
 * dette svar. En slukket feature optræder ikke og kan derfor ikke få et
 * nav-link — inert, ikke skjult-men-aktiv.
 */
export function useFeatures(): FeaturesState {
  const [state, setState] = useState<Omit<FeaturesState, "retry">>({
    features: [],
    loading: true,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      setState((current) => ({ ...current, loading: true, error: null }));
      apiGet<FeatureInfo[]>("/api/features")
        .then((features) => {
          if (!cancelled) setState({ features, loading: false, error: null });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const error =
            err instanceof ApiError && err.status === 401
              ? ("unauthorized" as const)
              : ("failed" as const);
          setState({ features: [], loading: false, error });
        });
    };
    window.addEventListener(FEATURES_UPDATED_EVENT, load);
    load();
    return () => {
      cancelled = true;
      window.removeEventListener(FEATURES_UPDATED_EVENT, load);
    };
  }, [attempt]);

  return { ...state, retry };
}
